import axios from 'axios';
import { getBrandFromHostname, PortalBrand } from '../utils/branding';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8090/api';

type LegalDocType =
  | 'T&C'
  | 'Form 7216'
  | 'CONSENT TO USE OF TAX RETURN INFORMATION'
  | 'CONSENT TO DISCLOSURE OF TAX RETURN INFORMATION';

function parseIdList(envValue: string | undefined, defaults: string[]): string[] {
  const fromEnv = (envValue || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
  const merged = fromEnv.concat(defaults);
  return Array.from(new Set(merged));
}

/**
 * TAL document_version ids (doc_version TAL-1.0).
 * Comma-separated env values allowed: prod first, then local.
 * Defaults cover both PocketBase environments.
 */
const TAL_DOCUMENT_ID_CANDIDATES: Partial<Record<LegalDocType, string[]>> = {
  'T&C': parseIdList(
    process.env.REACT_APP_TAL_TC_DOCUMENT_ID,
    // prod, local
    ['1ag79o70nx', '7moj7xbsnk']
  ),
  'CONSENT TO USE OF TAX RETURN INFORMATION': parseIdList(
    process.env.REACT_APP_TAL_CONSENT_USE_DOCUMENT_ID,
    ['jgwp1n6fb0', 'bgkve4azah']
  ),
  'CONSENT TO DISCLOSURE OF TAX RETURN INFORMATION': parseIdList(
    process.env.REACT_APP_TAL_CONSENT_DISCLOSURE_DOCUMENT_ID,
    ['f2rvlltk36', 'e981qf64dt']
  ),
};

const TAL_DOCUMENT_VERSION =
  process.env.REACT_APP_TAL_DOCUMENT_VERSION || 'TAL-1.0';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
  const tokenKey = process.env.REACT_APP_TOKEN_KEY || 'client_token';
  const token = localStorage.getItem(tokenKey);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface TermsConditionsRecord {
  id: string;
  red_rock_id: string;
  client_id: string;
  doc_type: 'T&C' | 'Consent' | 'Form 7216' | 'Privacy Policy' | 'CONSENT TO USE OF TAX RETURN INFORMATION' | 'CONSENT TO DISCLOSURE OF TAX RETURN INFORMATION';
  doc_version: string;
  doc_id: string;
  signed_data: string;
  full_doc_text: string;
  ip_address: string;
  manual_signature_option: boolean;
  /** True when signed against TAL Acquisition legal docs */
  is_tal_acquisite?: boolean;
  created: string;
  updated: string;
}

export interface DocumentVersion {
  id: string;
  doc_version: string;
  document_data: string; // This is the filename, we need to fetch the actual content
  doc_type: 'T&C' | 'Form 7216' | 'Privacy Policy' | 'Consent' | 'CONSENT TO USE OF TAX RETURN INFORMATION' | 'CONSENT TO DISCLOSURE OF TAX RETURN INFORMATION';
  created: string;
  updated: string;
}

export interface TermsAcceptanceData {
  red_rock_id: string;
  client_id: string;
  document_version: string; // This will be the ID from document_version table
  signed_data: string;
  signed_date?: string; // Date when signed
  ip_address: string;
  manual_signature_option: boolean;
  signature?: File; // Signature file to upload
  signature_device_type?: 'desktop' | 'tablet' | 'mobile'; // Device type used for signature
  name?: string; // Typed name (when name option is used instead of signature)
  /** Set true when accepting TAL Acquisition T&C / consents */
  is_tal_acquisite?: boolean;
  checkbox_data?: {
    agree_terms?: boolean;
    agree_privacy?: boolean;
    agree_marketing?: boolean;
    consent_duration?: number; // Duration in years for consent forms (1-5)
  };
}

class TermsConditionsService {
  // Red Rock document version (default 1.0)
  private getCurrentDocumentVersion(): string {
    return process.env.REACT_APP_DOCUMENT_VERSION || '1.0';
  }

  // Check if a string is a filename (has file extension)
  private isFilename(str: string): boolean {
    const commonExtensions = ['.html', '.txt', '.md', '.pdf', '.doc', '.docx'];
    return commonExtensions.some(ext => str.toLowerCase().endsWith(ext));
  }

  private async hydrateDocumentFile(document: DocumentVersion): Promise<DocumentVersion> {
    if (document.document_data && this.isFilename(document.document_data)) {
      try {
        const fileResponse = await api.get(
          `/files/document_version/${document.id}/${document.document_data}`,
          { responseType: 'text' }
        );
        document.document_data = fileResponse.data;
      } catch (fileError) {
        console.error('Failed to fetch file content:', fileError);
      }
    }
    return document;
  }

  private async fetchDocumentById(id: string): Promise<DocumentVersion | null> {
    if (!id) return null;
    try {
      const response = await api.get(`/collections/document_version/records/${id}`);
      return response.data || null;
    } catch {
      return null;
    }
  }

  private async fetchDocumentByTypeAndVersion(
    docType: LegalDocType,
    version: string
  ): Promise<DocumentVersion | null> {
    const filter = `doc_type="${docType}" && doc_version="${version}"`;
    const response = await api.get(
      `/collections/document_version/records?filter=${encodeURIComponent(filter)}`
    );
    if (response.data.items && response.data.items.length > 0) {
      return response.data.items[0];
    }
    return null;
  }

  /**
   * Fetch legal document HTML.
   * TAL brand (any device on TAL host): dedicated TAL-1.0 document ids.
   * Red Rock: doc_type + REACT_APP_DOCUMENT_VERSION (1.0).
   */
  async getDocumentContent(
    docType: LegalDocType,
    brand: PortalBrand = getBrandFromHostname()
  ): Promise<DocumentVersion | null> {
    try {
      if (brand === 'tal') {
        // Try prod then local ids (whichever PB is connected)
        const candidateIds = TAL_DOCUMENT_ID_CANDIDATES[docType] || [];
        for (const talId of candidateIds) {
          const byId = await this.fetchDocumentById(talId);
          if (byId) {
            return this.hydrateDocumentFile(byId);
          }
        }
        // Shared fallback: any record with doc_version TAL-1.0
        const byTalVersion = await this.fetchDocumentByTypeAndVersion(
          docType,
          TAL_DOCUMENT_VERSION
        );
        if (byTalVersion) {
          return this.hydrateDocumentFile(byTalVersion);
        }
      }

      const document = await this.fetchDocumentByTypeAndVersion(
        docType,
        this.getCurrentDocumentVersion()
      );
      if (document) {
        return this.hydrateDocumentFile(document);
      }
      return null;
    } catch (error: any) {
      console.error('Failed to fetch document content:', error);
      return null;
    }
  }

  async getTermsForClient(clientId: string): Promise<TermsConditionsRecord[]> {
    try {
      const filter = `client_id="${clientId}"`;
      const response = await api.get(`/collections/client_terms_conditions/records?filter=${encodeURIComponent(filter)}`);
      return response.data.items || [];
    } catch (error: any) {
      console.error('Failed to fetch terms and conditions:', error);
      return [];
    }
  }

  async getTermsForCustomer(clientId: string, redRockId: string): Promise<TermsConditionsRecord[]> {
    try {
      const filter = `client_id="${clientId}" && red_rock_id="${redRockId}"`;
      const response = await api.get(`/collections/client_terms_conditions/records?filter=${encodeURIComponent(filter)}`);
      return response.data.items || [];
    } catch (error: any) {
      console.error('Failed to fetch terms and conditions for customer:', error);
      return [];
    }
  }

  /**
   * Convert base64 data URL to File object
   */
  private dataURLtoFile(dataurl: string, filename: string): File {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  /**
   * Detect if the device is an iPad.
   * Handles both older iPad (userAgent contains "iPad") and iPadOS 13+
   * where Safari reports as Mac with multi-touch.
   */
  isIpad(): boolean {
    if (typeof navigator === 'undefined') {
      return false;
    }
    const ua = navigator.userAgent;
    // Older iPad: userAgent contains "iPad"
    if (ua.includes('iPad')) {
      return true;
    }
    // iPadOS 13+: Safari reports platform as "MacIntel" with multi-touch
    if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
      return true;
    }
    return false;
  }

  /**
   * Detect device type based on screen width and touch support
   */
  detectDeviceType(): 'desktop' | 'tablet' | 'mobile' {
    if (typeof window === 'undefined') {
      return 'desktop';
    }

    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const width = window.innerWidth;

    if (width < 768) {
      return 'mobile';
    } else if (width < 1024 && hasTouch) {
      return 'tablet';
    } else {
      return 'desktop';
    }
  }

  async acceptTerms(data: TermsAcceptanceData): Promise<TermsConditionsRecord> {
    try {
      // Prepare FormData for file upload
      const formData = new FormData();

      // Add signature file if provided
      if (data.signature) {
        formData.append('signature', data.signature);
      }

      // Prepare the record data (excluding signature file)
      const recordData: any = {
        red_rock_id: data.red_rock_id,
        client_id: data.client_id,
        document_version: data.document_version,
        signed_data: data.signed_data,
        signed_date: data.signed_date || data.signed_data,
        ip_address: data.ip_address,
        manual_signature_option: data.manual_signature_option,
      };

      // Add signature_device_type if provided
      if (data.signature_device_type) {
        recordData.signature_device_type = data.signature_device_type;
      }

      // TAL Acquisition signatures — used to decide whether to re-prompt the modal
      if (typeof data.is_tal_acquisite === 'boolean') {
        recordData.is_tal_acquisite = data.is_tal_acquisite;
      }

      // Add name if provided (when typed name option is used)
      if (data.name) {
        recordData.name = data.name;
      }

      // Add checkbox_data if provided
      if (data.checkbox_data) {
        recordData.checkbox_data = JSON.stringify(data.checkbox_data);
      }

      // Add all record fields to FormData
      // For relation fields (like document_version), just append the ID as a string
      Object.keys(recordData).forEach((key) => {
        const value = recordData[key];
        if (value !== undefined && value !== null) {
          // Relation fields should be sent as just the ID string
          formData.append(key, value.toString());
        }
      });

      // Make the request with FormData
      // Note: Don't set Content-Type header - let browser set it with boundary for multipart/form-data
      const tokenKey = process.env.REACT_APP_TOKEN_KEY || 'client_token';
      const token = localStorage.getItem(tokenKey);

      // Use axios directly for FormData (api instance might have issues with FormData)
      const response = await axios.post(
        `${API_BASE_URL}/collections/client_terms_conditions/records`,
        formData,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            // Don't set Content-Type - browser will set it with boundary for multipart/form-data
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Failed to accept terms:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to accept terms and conditions';
      throw new Error(errorMessage);
    }
  }

  /**
   * Whether the 3-step terms modal can be skipped for this company.
   *
   * Red Rock: hide modal when ≥3 client_terms_conditions rows exist for the company.
   * TAL Acquisition (TAL or Red Rock portal): hide only when ≥3 rows exist with
   * is_tal_acquisite === true. Existing rows with the flag false/missing still show the modal.
   */
  async checkTermsAccepted(
    _clientId: string,
    redRockId: string,
    options?: { requireTalAcquisition?: boolean }
  ): Promise<boolean> {
    try {
      const filter = `red_rock_id="${redRockId}"`;
      const response = await api.get(
        `/collections/client_terms_conditions/records?filter=${encodeURIComponent(filter)}&perPage=200`
      );
      const items: TermsConditionsRecord[] = response.data.items || [];

      if (options?.requireTalAcquisition) {
        const talRows = items.filter((item) => item.is_tal_acquisite === true);
        return talRows.length >= 3;
      }

      return items.length >= 3;
    } catch (error: any) {
      console.error('Failed to check terms acceptance:', error);
      console.error('Error details:', error.response?.data);
      return false;
    }
  }

  async getClientIP(): Promise<string> {
    try {
      // Get IP from a public service
      const ipApiUrl = process.env.REACT_APP_IP_API_URL || 'https://api.ipify.org?format=json';
      const response = await fetch(ipApiUrl);
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error('Failed to get IP address:', error);
      return '127.0.0.1'; // fallback
    }
  }
}

export const termsConditionsService = new TermsConditionsService();

